import { ArrowBack, DeleteForever, Lock, LockOpen, PictureAsPdf } from '@mui/icons-material'
import { Button, Divider, Grid } from '@mui/material'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { FedExShipmentDto } from 'dtos/fedEx'
import { MichiganOutgoingTitleDto } from 'dtos/michiganOutgoingTitles'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import ManageVehiclesCard from './ManageVehiclesCard'
import MichiganOutgoingTitlesDetailsCard from './MichiganOutgoingTitlesDetailsCard'
import OutgoingTitlesGroupFedexLabelCard from './OutgoingTitlesGroupFedexLabelCard'

export default function MichiganOutgoingTitlesDetails() {
  const { michiganOutgoingTitleGuid } = useParams()
  const layout = useLayout()
  const navigate = useNavigate()

  const [fedExShipment, setFedExShipment] = useState<FedExShipmentDto | undefined>()
  const [michiganOutgoingTitle, setMichiganOutgoingTitle] = useState<MichiganOutgoingTitleDto>(new MichiganOutgoingTitleDto())

  useEffect(() => {
    if (!michiganOutgoingTitleGuid) return

    // At the time of writing, Michigan Outgoing Title Details does not have
    // a save button. When a user navigates to /michigan-outgoing-titles/new,
    // create a new Michigan Outgoing Title and quickly navigate the user to
    // /michigan-outgoing-title/:michiganOutgoingTitleGuid.
    // Maxwell Sommer 10/18/2025
    const isNew: boolean = michiganOutgoingTitleGuid.trim().toUpperCase() === 'NEW'

    layout.setIsLayoutLoading(true)
    if (isNew) {
      controllers.michiganOutgoingTitles
        .createMichiganOutgoingTitleGroup(new MichiganOutgoingTitleDto())
        .then(res => {
          enqueueSnackbar('Michigan Outgoing Title Created Successfully!', { variant: 'success' })
          navigate(`/michigan-outgoing-titles/${encodeURIComponent(res.value.michiganOutgoingTitleGuid!)}`, { replace: true })
        })
        .catch(() => navigate(-1))
    } else {
      controllers.michiganOutgoingTitles
        .getMichiganOutgoingTitleGroup(michiganOutgoingTitleGuid)
        .then(res => {
          setMichiganOutgoingTitle(res.value)
        })
        .finally(() => {
          layout.setIsLayoutLoading(false)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [michiganOutgoingTitleGuid])

  useEffect(() => {
    layout.setTitle('Michigan Outgoing Title Details')

    return () => {
      layout.setTitle('')
    }
  }, [layout])

  return (
    <>
      <Grid container spacing={1} alignItems='flex-start'>
        <Grid item xs={12}>
          <MichiganOutgoingTitlesDetailsCard fedExShipment={fedExShipment} michiganOutgoingTitle={michiganOutgoingTitle}>
            <Grid item>
              <Button
                onClick={() => {
                  navigate('/michigan-outgoing-titles')
                }}
                color='primary'
                startIcon={<ArrowBack />}
                variant='text'
              >
                BACK
              </Button>
            </Grid>

            <Grid item sx={{ alignSelf: 'stretch' }}>
              <Divider orientation='vertical' />
            </Grid>

            <Grid item>
              <Button
                disabled={!michiganOutgoingTitle.michiganOutgoingTitleGuid || !michiganOutgoingTitle.isLocked || !fedExShipment?.fedExShipmentGuid}
                endIcon={<PictureAsPdf />}
                onClick={() => {
                  layout.setIsLayoutLoading(true)
                  controllers.fedEx.generateFedExLabels([fedExShipment!.fedExShipmentGuid!]).finally(() => {
                    layout.setIsLayoutLoading(false)
                  })
                }}
                variant='text'
              >
                GENERATE OUTGOING TITLES GROUP FEDEX LABEL
              </Button>
            </Grid>

            <Grid item>
              <Button
                endIcon={michiganOutgoingTitle.isLocked ? <LockOpen /> : <Lock />}
                onClick={() => {
                  layout.setIsLayoutLoading(true)
                  if (michiganOutgoingTitle.isLocked === true) {
                    controllers.michiganOutgoingTitles
                      .unlockMichiganOutgoingTitleGroup(michiganOutgoingTitle.michiganOutgoingTitleGuid!)
                      .then(response => {
                        setMichiganOutgoingTitle(response.value)
                        enqueueSnackbar('Michigan Outgoing Title Unlocked Successfully!', { variant: 'success' })
                        setFedExShipment(undefined)
                      })
                      .finally(() => {
                        layout.setIsLayoutLoading(false)
                      })
                  } else {
                    controllers.michiganOutgoingTitles
                      .lockMichiganOutgoingTitleGroup(michiganOutgoingTitle.michiganOutgoingTitleGuid!)
                      .then(response => {
                        setMichiganOutgoingTitle(response.value)
                        enqueueSnackbar('Michigan Outgoing Title Locked Successfully!', { variant: 'success' })
                      })
                      .finally(() => {
                        layout.setIsLayoutLoading(false)
                      })
                  }
                }}
                variant='text'
              >
                {michiganOutgoingTitle.isLocked ? 'UNLOCK' : 'LOCK'}
              </Button>
            </Grid>

            <Grid item>
              <Button
                color='error'
                disabled={!michiganOutgoingTitleGuid || michiganOutgoingTitle?.isLocked}
                endIcon={<DeleteForever />}
                variant='text'
                onClick={() => {
                  if (window.confirm('Are you sure you would like to delete this Michigan Outgoing Title? This cannot be undone.')) {
                    layout.setIsLayoutLoading(true)
                    controllers.michiganOutgoingTitles.deleteMichiganOutgoingTitleGroup(michiganOutgoingTitleGuid!).then(() => {
                      enqueueSnackbar('Michigan Outgoing Title Deleted Successfully!', { variant: 'success' })
                      navigate('/michigan-outgoing-titles', { replace: true })
                    })
                  }
                }}
              >
                DELETE
              </Button>
            </Grid>
          </MichiganOutgoingTitlesDetailsCard>
        </Grid>

        <Grid item container xs={12} alignItems='flex-start'>
          <Grid item xs={8}>
            <ManageVehiclesCard michiganOutgoingTitle={michiganOutgoingTitle} setMichiganOutgoingTitle={setMichiganOutgoingTitle} />
          </Grid>

          <Grid item xs={4}>
            <OutgoingTitlesGroupFedexLabelCard
              fedExShipment={fedExShipment}
              setFedExShipment={setFedExShipment}
              isLocked={michiganOutgoingTitle.isLocked ?? true}
              michiganOutgoingTitleGuid={michiganOutgoingTitle.michiganOutgoingTitleGuid}
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  )
}
